/**
 * The follow-up question in the chat (stage 3): „Für das Gebäude brauche ich eine Liegenschaft — welche?"
 *
 * Everything shown here was computed by the server: text, candidates, options. The block turns
 * them into buttons. A click is a message (`chatSend.ts`) — the same one the user could have
 * typed; free text remains possible, the input line is not locked.
 *
 * Candidates show `n. label`; the click sends `n` as text — the server interprets "1" against the
 * question's candidates (rule 1 of answer interpretation), hence no ids in the block and none in
 * the text.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { answerMessage, requestChatSend } from './chatSend';
import { GuideCollapsed } from './GuideCollapsed';
import { GuideQuestionForm } from './GuideQuestionForm';
import { guideLocale, isQuestionBlock, type QuestionBlockData } from './guideBlocks';

export interface GuideQuestionBlockProps {
    resource: unknown;
    className?: string;
    /** True once a newer message exists: the card folds to one line. */
    collapsed?: boolean;
}

const OPTION_VARIANT: Record<QuestionBlockData['options'][number]['id'], 'default' | 'secondary' | 'outline' | 'ghost'> = {
    yes: 'default',
    create: 'secondary',
    choose: 'secondary',
    no: 'outline',
    cancel: 'ghost',
};

/**
 * Question cards are switched off for now (user decision 28.09.2026: the follow-up runs through
 * the chat line alone — the server's text mirror carries the question). The confirmation card
 * stays, since only it can trigger the write. Tests turn the cards on explicitly.
 *
 * A block with `presentation: 'card'` shows regardless (29.09.2026): the prompt-tool plan asks for
 * a name and for choices, not for an object the server would suggest.
 */
let questionCardsEnabled = false;
export function setGuideQuestionCardsEnabled(enabled: boolean): void {
    questionCardsEnabled = enabled;
}

export function GuideQuestionBlock({ resource, className, collapsed = false }: GuideQuestionBlockProps) {
    const { i18n } = useTranslation();
    const [sent, setSent] = useState<string | null>(null);
    if (!isQuestionBlock(resource)) return null;
    if (!questionCardsEnabled && resource.data.presentation !== 'card') return null;
    const data = resource.data;
    const locale = guideLocale(i18n?.language);
    const text = data.text[locale] || data.text.de;

    const send = (label: string) => {
        if (sent) return;
        const ok = requestChatSend(answerMessage({ label, planId: data.planId, questionKey: data.questionKey }));
        if (ok) setSent(label);
    };

    return (
        <GuideCollapsed collapsed={collapsed} summary={text}>
        <div
            className={cn('my-2 flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-3', className)}
            data-testid='guide-question-block'
            data-plan-id={data.planId}
            data-question-key={data.questionKey}
            role='group'
            aria-label={text}
        >
            <div className='text-sm leading-[1.45]'>{text}</div>

            {data.candidates && data.candidates.length > 0 && (
                <ol className='m-0 flex list-none flex-col gap-1 p-0' data-testid='guide-question-candidates'>
                    {data.candidates.map((candidate) => (
                        <li key={candidate.n}>
                            <button
                                type='button'
                                disabled={!!sent}
                                onClick={() => send(String(candidate.n))}
                                className={cn(
                                    buttonVariants({ variant: 'outline', size: 'sm' }),
                                    'w-full justify-start gap-2 text-left font-normal',
                                    sent === String(candidate.n) && 'ring-2 ring-ring',
                                )}
                            >
                                <span className='w-5 shrink-0 font-mono text-xs text-muted-foreground'>{candidate.n}.</span>
                                <span className='min-w-0 break-words'>{candidate.label}</span>
                            </button>
                        </li>
                    ))}
                </ol>
            )}

            {data.fields && data.fields.length > 0 && (
                <GuideQuestionForm
                    fields={data.fields}
                    locale={locale}
                    disabled={!!sent}
                    onSubmit={(message) => {
                        if (sent) return false;
                        const ok = requestChatSend(answerMessage({ label: message, planId: data.planId, questionKey: data.questionKey }));
                        if (ok) setSent(message);
                        return ok;
                    }}
                />
            )}

            {data.options.length > 0 && (
                <div className='flex flex-wrap gap-1.5' data-testid='guide-question-options'>
                    {data.options.map((option) => {
                        const label = option.label[locale] || option.label.de;
                        return (
                            <button
                                key={option.id}
                                type='button'
                                disabled={!!sent}
                                data-option={option.id}
                                onClick={() => send(label)}
                                className={cn(
                                    buttonVariants({ variant: OPTION_VARIANT[option.id] ?? 'secondary', size: 'sm' }),
                                    sent === label && 'ring-2 ring-ring',
                                )}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
        </GuideCollapsed>
    );
}
